// Power-up parks (spec GP-24, D-33): powers lie at seeded spots on the road and are picked up by
// driving over them, one held at a time. Same rules for everyone: what a pickup gives is drawn
// with one seeded draw for every role, so the draw itself never tells the roles apart, and a
// power never reveals or protects a role. Speed boost and wiper for anyone; the fake breakdown for
// agberos only. Pure and deterministic like the rest of the simulation.
import { getMap, TILE } from '@wahala/maps';
import { dist2 } from './geometry.js';
import { nextInt } from './rng.js';
/** How many pickups lie on the road at once. */
export const PICKUPS_ON_ROAD = 4;
export const PICKUP_RADIUS = 0.6 * TILE;
export const RESPAWN_MS = 15_000;
export const BOOST_MS = 4_000;
export const BOOST_FACTOR = 1.5;
export const WIPER_MS = 10_000;
/** The wiper clears the road around you: vision this many times wider while it runs. */
export const WIPER_FACTOR = 1.5;
export const DECOY_MS = 20_000;
const CREW_POWERS = ['boost', 'wiper'];
const AGBERO_POWERS = ['boost', 'wiper', 'decoy'];
export function createPowers(s) {
    if (s.config.mode !== 'powers')
        return undefined;
    const p = { pickups: [], respawnAt: [], nextId: 1, held: {}, boostUntil: {}, wiperUntil: {} };
    for (const id of s.order) {
        p.held[id] = null;
        p.boostUntil[id] = 0;
        p.wiperUntil[id] = 0;
    }
    const spots = getMap(s.mapId).powerSpots.length;
    for (let i = 0; i < Math.min(PICKUPS_ON_ROAD, spots); i++)
        spawnPickup(s, p);
    return p;
}
/** Lay one pickup on a free spot, chosen with the match's seeded RNG. */
function spawnPickup(s, p) {
    const spots = getMap(s.mapId).powerSpots;
    const taken = new Set(p.pickups.map((x) => x.spot));
    const free = spots.map((_, i) => i).filter((i) => !taken.has(i));
    if (free.length === 0)
        return;
    const spot = free[nextInt(s.rng, free.length)];
    const at = spots[spot];
    p.pickups.push({ id: `w${p.nextId++}`, spot, pos: { x: at.x, y: at.y } });
}
/** One seeded draw for every role (the same RNG use either way); agberos map it onto three powers. */
function drawPower(s, who) {
    const n = nextInt(s.rng, 6);
    const pool = who.role === 'impostor' ? AGBERO_POWERS : CREW_POWERS;
    return pool[n % pool.length];
}
export const boosted = (s, id) => (s.powers?.boostUntil[id] ?? 0) > s.now;
export const wiping = (s, id) => (s.powers?.wiperUntil[id] ?? 0) > s.now;
/** Each tick on the road: pickups come back, fake breakdowns clear, kekes drive over pickups. */
export function tickPowers(s, events) {
    const p = s.powers;
    if (!p)
        return;
    s.bodies = s.bodies.filter((b) => !b.decoy || (b.expiresAt ?? 0) > s.now);
    const due = p.respawnAt.filter((at) => at <= s.now).length;
    if (due) {
        p.respawnAt = p.respawnAt.filter((at) => at > s.now);
        for (let i = 0; i < due; i++)
            spawnPickup(s, p);
    }
    for (const id of s.order) {
        const who = s.players[id];
        if (!who.alive || who.departed || who.vent !== null || p.held[id])
            continue;
        const i = p.pickups.findIndex((x) => dist2(x.pos, who.pos) <= PICKUP_RADIUS * PICKUP_RADIUS);
        if (i < 0)
            continue;
        p.pickups.splice(i, 1);
        p.respawnAt.push(s.now + RESPAWN_MS);
        const power = drawPower(s, who);
        p.held[id] = power;
        events.push({ t: 'power.picked', playerId: id, power });
    }
}
/** A meeting stops running powers (a held one is kept for later). */
export function stopPowers(s) {
    const p = s.powers;
    if (!p)
        return;
    for (const id of s.order) {
        p.boostUntil[id] = 0;
        p.wiperUntil[id] = 0;
    }
}
/** Who a fake breakdown is dressed as: a seeded pick of another keke still on the road. */
function decoyOf(s, user) {
    const taken = new Set(s.bodies.map((b) => b.id));
    const pool = s.order
        .map((id) => s.players[id])
        .filter((x) => x.id !== user.id && x.alive && !x.departed && !taken.has(x.id));
    return pool.length ? pool[nextInt(s.rng, pool.length)] : null;
}
/** Can this player use what they hold right now? (a fake breakdown needs someone to dress as) */
export function canUsePower(s, who) {
    const power = s.powers?.held[who.id];
    if (!power)
        return false;
    if (power !== 'decoy')
        return true;
    const taken = new Set(s.bodies.map((b) => b.id));
    return s.order.some((id) => {
        const x = s.players[id];
        return x.id !== who.id && x.alive && !x.departed && !taken.has(x.id);
    });
}
export function usePower(s, who, events) {
    const p = s.powers;
    const power = p.held[who.id];
    p.held[who.id] = null;
    if (power === 'boost')
        p.boostUntil[who.id] = s.now + BOOST_MS;
    else if (power === 'wiper')
        p.wiperUntil[who.id] = s.now + WIPER_MS;
    else {
        const as = decoyOf(s, who);
        if (as)
            s.bodies.push({
                id: as.id,
                playerId: as.id,
                pos: { ...who.pos },
                createdAt: s.now,
                reported: false,
                decoy: true,
                expiresAt: s.now + DECOY_MS,
            });
    }
    events.push({ t: 'power.used', playerId: who.id, power });
}
/** A real knock on a keke wearing a fake breakdown clears the fake, so a body id stays unique. */
export function dropDecoyOf(s, playerId) {
    if (s.powers)
        s.bodies = s.bodies.filter((b) => !(b.decoy && b.id === playerId));
}
//# sourceMappingURL=powers.js.map